import moment from "moment";
import "../../../utils/momentDe"; // zentrale de-Locale-Quelle (Mo-Wochenstart, ISO-KW)

/**
 * Reine Datums-Ableitungen für die TWV/Frist-Zeile (SP7). Bewusst als DOM-
 * freies `.ts`-Modul, damit der (node-only) Vitest-Runner sie direkt prüft.
 *
 * Ersetzt die native `<input type="date">`-Anzeige durch ein sprechendes
 * „Di., 07.07.2026 · übermorgen" (Review B12): absolutes Datum + relatives Wort.
 */

/** Farb-/Dringlichkeitston der relativen Info (Review B12: „überfällig rot"). */
export type RelativeTone = "overdue" | "today" | "soon" | "normal";

export interface TwvDateView {
    /** Absolute Anzeige, dt. Locale, Jahr NUR wenn ≠ aktuelles Jahr:
     *  „Di., 07.07." (dieses Jahr) / „Di., 07.07.2027". Dient als Voll-Titel/a11y. */
    date: string;
    /** Kalenderblatt: Tag ohne führende Null („7"). */
    day: string;
    /** Kalenderblatt: Monatskürzel, dt. Locale („Juli", „Jan."). */
    monthShort: string;
    /** Wochentag kurz („Di."). */
    weekday: string;
    /** Jahr NUR, wenn ≠ aktuelles Jahr (sonst null → nicht anzeigen). */
    year: string | null;
    /** Relatives Wort: „heute" | „morgen" | „übermorgen" | „Freitag" |
     *  „nächste Woche Di." | „überfällig" | "" (nichts). */
    relative: string;
    /** Ton für die Einfärbung. */
    tone: RelativeTone;
}

/** Auf reines YYYY-MM-DD normalisieren (schneidet eine evtl. Zeitkomponente ab). */
export function toIsoDate(v?: string | null): string {
    return v ? String(v).slice(0, 10) : "";
}

/**
 * Relatives Wort zu einem ISO-Datum (YYYY-MM-DD) bezogen auf `now`:
 *  - Vergangenheit → „überfällig" (overdue)
 *  - heute/morgen/übermorgen
 *  - übrige Tage DIESER ISO-Woche → Wochentag ausgeschrieben („Freitag")
 *  - nächste ISO-Woche → „nächste Woche Di."
 *  - weiter entfernt → "" (kein relatives Wort, Review §3.8)
 */
export function relativeWord(
    iso: string,
    now: Date = new Date(),
): { relative: string; tone: RelativeTone } {
    const target = moment(toIsoDate(iso), "YYYY-MM-DD", true).startOf("day");
    if (!target.isValid()) return { relative: "", tone: "normal" };

    const today = moment(now).startOf("day");
    const dayDiff = target.diff(today, "days");

    if (dayDiff < 0) return { relative: "überfällig", tone: "overdue" };
    if (dayDiff === 0) return { relative: "heute", tone: "today" };
    if (dayDiff === 1) return { relative: "morgen", tone: "soon" };
    if (dayDiff === 2) return { relative: "übermorgen", tone: "soon" };

    // Ab 3 Tagen: über die ISO-Woche entscheiden (jahresgrenzen-fest).
    const weekDiff = target
        .clone()
        .startOf("isoWeek")
        .diff(today.clone().startOf("isoWeek"), "weeks");
    if (weekDiff === 0) return { relative: target.format("dddd"), tone: "normal" };
    if (weekDiff === 1) return { relative: `nächste Woche ${target.format("ddd")}`, tone: "normal" };
    return { relative: "", tone: "normal" };
}

/**
 * Vollständige Anzeige-Ableitung für ein TWV-/Frist-Datum: absolutes Datum
 * (dt. Locale) + relatives Wort + Ton. `null`, wenn kein Datum gesetzt ist.
 */
export function twvDateView(v?: string | null, now: Date = new Date()): TwvDateView | null {
    const iso = toIsoDate(v);
    if (!iso) return null;
    const m = moment(iso, "YYYY-MM-DD", true);
    if (!m.isValid()) return null;
    const { relative, tone } = relativeWord(iso, now);
    // Jahr nur zeigen, wenn es NICHT das aktuelle Jahr ist (Review §T2).
    const sameYear = m.year() === moment(now).year();
    const year = sameYear ? null : String(m.year());
    return {
        date: sameYear ? m.format("dd., DD.MM.") : m.format("dd., DD.MM.YYYY"),
        day: String(m.date()),
        monthShort: m.format("MMM"),
        weekday: m.format("dd."),
        year,
        relative,
        tone,
    };
}
